import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  KycType,
  StakeholderKycDetail,
} from "../../../api/enrichedStakeholder";

import { getEnrichedStakeholderById } from "../logic/stakeholderSlice";
import { updateStakeholderKycDetails } from "../logic/stakeholderThunks";
import { showErrorToast } from "../../../lib/toast";
import {v4 as uuidv4} from 'uuid';

interface StakeholderKycProps {
  stakeholderId: string;
}

const StakeholderKyc: React.FC<StakeholderKycProps> = ({ stakeholderId }) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId)
  );

  if (!enrichedStakeholder) {
    return null;
  }

  const kycDetails: StakeholderKycDetail[] =
    enrichedStakeholder.kyc_details || [];

  const panDetail = kycDetails.find((k) => k.kyc_type === KycType.PAN);
  const aadhaarDetail = kycDetails.find(
    (k) => k.kyc_type === KycType.AADHAAR
  );

  const handleSaveKyc = async (
    kycType: KycType,
    newValue: string | null
  ): Promise<boolean> => {
    if (!caseId) return false;

    setIsSaving(true);

    try {
      const valStr = newValue?.trim() || "";

      let updatedKycDetails: StakeholderKycDetail[] = [];

      const existingIndex = kycDetails.findIndex(
        (k) => k.kyc_type === kycType
      );

      if (existingIndex >= 0) {
        updatedKycDetails = kycDetails.map((k) =>
          k.kyc_type === kycType ? { ...k, kyc_value: valStr } : k
        );
      } else {
        updatedKycDetails = [
          ...kycDetails,
          {
            kyc_id: uuidv4(),
            kyc_type: kycType,
            kyc_value: valStr,
          },
        ];
      }

      await dispatch(
        updateStakeholderKycDetails({
          caseId,
          stakeholderId,
          request: {
            kyc_details: updatedKycDetails,
          },
        })
      ).unwrap();

      setEditingField(null);
      return true;
    } catch {
      showErrorToast("Failed to update KYC details. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="KYC Identifiers">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="PAN"
          value={panDetail?.kyc_value ?? null}
          type="pan"
          isEditable={true}
          disabled={isSaving}
          isEditing={editingField === "pan"}
          onEditStart={() => setEditingField("pan")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSaveKyc(KycType.PAN, value)}
        />

        <EditableField
          label="Aadhaar"
          value={aadhaarDetail?.kyc_value ?? null}
          type="aadhaar"
          isEditable={true}
          disabled={isSaving}
          isEditing={editingField === "aadhaar"}
          onEditStart={() => setEditingField("aadhaar")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSaveKyc(KycType.AADHAAR, value)}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default StakeholderKyc;